<template>
	<div class="grid w-full grid-cols-1 gap-8 px-3 py-6 xl:grid-cols-2 place-content-between">
		<!-- Left Column -->
		<div class="w-full min-h-48">
			<a
				v-if="hasMap"
				:href="directionsUrl"
				target="_blank"
				class="block pb-1"
			>
				<img
					:src="mapImageUrl"
					alt="Map"
					class="w-full"
				>
			</a>
			<div class="min-h-7">
				{{ address }}
			</div>
		</div>
		<!-- Right Column -->
		<div class="grid w-full grid-cols-1 gap-6 gap-y-14">
			<!-- About -->
			<div class="md:flex min-h-64">
				<h3 class="flex items-start mb-2 text-xl md:w-1/3 h-7">
					<img
						src="@/assets/icons/about.svg"
						class="w-6 h-6 mt-1 mr-2"
						alt="About Icon"
					>
					About
				</h3>
				<div
					v-if="hasAbout"
					class="md:w-2/3"
					v-html="info.about"
				/>
				<div v-else>
					<p class="flex items-center w-full italic text-gray-500 h-7">
						No information available.
					</p>
				</div>
			</div>
			<!-- Amenities -->
			<div class="md:flex min-h-40">
				<h3 class="flex mb-2 text-xl md:w-1/3 h-7">
					<img
						src="@/assets/icons/amenities.svg"
						class="h-6 w-6 mr-2 mt-0.5"
						alt="Amenities Icon"
					>
					Amenities
				</h3>
				<ul
					v-if="hasAmenities"
					class="ml-4 list-disc md:w-2/3"
				>
					<li
						v-for="(feature, index) in business.features"
						:key="index"
					>
						{{ business.features[index].name }}
					</li>
				</ul>
				<div v-else>
					<p class="flex items-center w-full italic text-gray-500 h-7">
						No amenities listed.
					</p>
				</div>
			</div>
			<!-- Hours -->
			<div class="md:flex min-h-64">
				<h3 class="flex mb-2 text-xl md:w-1/3 h-7">
					<img
						src="@/assets/icons/hours.svg"
						class="h-6 w-6 mr-2 mt-0.5"
						alt="Hours Icon"
					>
					Hours
				</h3>
				<div
					v-if="hasHours"
					class="flex flex-col md:w-2/3"
				>
					<div
						v-for="(weekday, index) in weekdays"
						:key="index"
						:class="{ 'font-bold' : parseInt(day) === index }"
						class="flex mb-2"
					>
						<div class="w-1/3 font-bold truncate">
							{{ weekday }}
						</div>
						<div class="flex w-2/3 truncate">
							<div>
								{{ timePrint(business.hours[index]) }}
							</div>
							<div
								v-if="business.is_open && parseInt(day) === index"
								class="p-1 px-2 ml-2 text-xs font-bold leading-4 text-white uppercase bg-green-500 rounded"
							>
								Open
							</div>
						</div>
					</div>
				</div>
				<div v-else>
					<p class="flex items-center w-full italic text-gray-500 h-7">
						No hours listed.
					</p>
				</div>
			</div>
			<!-- Links -->
			<div class="md:flex">
				<h3 class="flex mb-2 text-xl md:w-1/3 h-7">
					<img
						src="@/assets/icons/findus.svg"
						class="h-6 w-6 mr-2 mt-0.5"
						alt="Find Us icon"
					>
					Find Us
				</h3>
				<ul
					v-if="hasLinks"
					class="p-0 md:w-2/3"
				>
					<li
						v-if="business.website"
						class="mb-2"
					>
						<a
							v-track-event:external.click="externalLinkClickTrackingData"
							class="flex"
							:href="business.website"
							target="_blank"
						>
							<img
								src="@/assets/icons/website.svg"
								alt="Website Icon - Where's Weed"
								class="w-6 h-6 mr-2"
							>
							<span>
								Website
							</span>
						</a>
					</li>
					<li
						v-if="business.facebook_url"
						class="mb-2"
					>
						<a
							class="flex"
							:href="'//www.facebook.com/' + business.facebook_url"
						>
							<img
								class="w-6 h-6 mr-2"
								src="@/assets/icons/facebook-color.svg"
								alt="Facebook Link"
							>
							<span>
								Facebook
							</span>
						</a>
					</li>
					<li
						v-if="business.twitter_url"
						class="mb-2"
					>
						<a
							class="flex"
							:href="'//www.twitter.com/' + business.twitter_url"
						>
							<img
								src="@/assets/icons/twitter-color.svg"
								class="w-6 h-6 mr-2"
								alt="Twitter Link"
							>
							<span>
								Twitter
							</span>
						</a>
					</li>
					<li
						v-if="business.instagram_url"
						class="mb-2"
					>
						<a
							class="flex"
							:href="'//www.instagram.com/' + business.instagram_url"
						>
							<img
								class="w-6 h-6 mr-2"
								src="@/assets/icons/instagram-color.svg"
								alt="Instagram Link"
							>
							<span>
								Instagram
							</span>
						</a>
					</li>
					<li
						v-if="business.pintrest_url"
						class="mb-2"
					>
						<a
							class="flex"
							:href="'//www.pintrest.com/' + business.pintrest_url"
						>
							<img
								class="w-6 h-6 mr-2"
								src="@/assets/icons/pinterest-color.svg"
								alt="Pintrest Link"
							>
							<span>
								Pinterest
							</span>
						</a>
					</li>
				</ul>
				<div v-else>
					<p class="flex items-center w-full italic text-gray-500 h-7">
						No socials linked.
					</p>
				</div>
			</div>

			<!-- Report / Claim Business -->
			<div class="flex justify-center mb-12 md:justify-right">
				<button
					type="button"
					:class="[
						'mt-3 w-32 hover:shadow-lg inline-flex justify-center md:ml-auto rounded-md border border-gray-300 shadow-sm px-4 py-2 text-base font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm mx-2'
					]"
					@click="showModal(REPORT_BUSINESS_MODAL)"
				>
					Report
				</button>
				<button
					v-if="!isClaimed"
					class="inline-flex justify-center px-4 py-2 mx-2 mt-3 text-base font-medium border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
					@click="handleClaimBusiness"
				>
					Claim Business
				</button>
			</div>
		</div>

		<ReportBusinessModal
			:modal-id="REPORT_BUSINESS_MODAL"
			@report-submitted="handleReportSubmitted"
		/>
	</div>
</template>
<script>
import {
	mapActions,
	mapGetters,
	mapMutations
} from 'vuex'

import ReportBusinessModal from '@/components/UI/modals/ReportBusinessModal'
import {
	REPORT_BUSINESS_MODAL
} from '@/constants/modal/names.js'
import {
	ERROR,
	SUCCESS
} from '@/constants/toast/type'
import { logError } from '@/utils/error-handling.js'
import { formatAddress } from '@/utils/formatText'
export default {
	components: {
		ReportBusinessModal
	},
	data() {
		return {
			isMounted: false,
			isClaimed: false,
			weekdays: [
				'Sunday',
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday'
			],
			REPORT_BUSINESS_MODAL
		}
	},
	computed: {
		...mapGetters('business', [
			'business',
			'info',
			'day'
		]),
		externalLinkClickTrackingData() {
			return {
				listingId: this.business.listing_id,
				destinationUrl: this.business.website
			}
		},
		directionsUrl() {
			return this.business?.directions_link
		},
		businessType() {
			return this.info?.type
		},
		mapImageUrl() {
			return this.business?.static_map
		},
		hasLinks() {
			return !!Object.keys(this.links).length
		},
		hasAbout() {
			return this.info?.about && !!this.info?.about.length
		},
		hasAmenities() {
			return this.business?.features && !!this.business?.features.length
		},
		hasHours() {
			return this.business?.hours && !!this.business?.hours.length
		},
		hasMap() {
			return typeof (this.business?.static_map) !== 'undefined' && this.business?.static_map
		},
		businessImageUrl() {
			return `/listing_images/120x120/${this.business.image}`
		},
		address() {
			const {
				address, city, state_short: state, zip
			} = this.business
			if (state === 'DC') {
				return formatAddress({
					address, city, zip
				})
			} else {
				return formatAddress({
					address, city, state, zip
				})
			}
		},
		links() {
			const links = {}
			const {
				website,
				facebook_url: facebookUrl,
				twitter_url: twitterUrl,
				pintrest_url: pintrestUrl,
				instagram_url: instagramUrl
			} = this.business
			if (website) { links.website = website }
			if (facebookUrl) { links.facebook = facebookUrl }
			if (twitterUrl) { links.twitter = twitterUrl }
			if (pintrestUrl) { links.pinterest = pintrestUrl }
			if (instagramUrl) { links.instagram = instagramUrl }
			return links
		}
	},
	mounted() {
		this.isMounted = true
		this.setIsClaimed()
	},
	beforeUpdate() {
		this.setIsClaimed()
	},
	methods: {
		...mapMutations('modal', [ 'showModal' ]),
		...mapActions('business', [ 'reportBusiness' ]),
		...mapMutations('toast', [ 'showToast' ]),
		setIsClaimed() {
			this.isClaimed = Number(this.business.creatorid) > 0
		},
		handleClaimBusiness() {
			const {
				type, name, address, address2, city, state_short: state, zip, website, phone, email
			} = this.business
			localStorage.setItem('businessPrefillData', JSON.stringify({
				type,
				name,
				address,
				address2,
				city,
				state,
				zip,
				website,
				phone,
				email
			}))
			this.$router.push(`/tour/${this.businessType}`)
		},
		// Function to Convert date to human readable
		timePrint(weekdayData) {
			if (!weekdayData) {
				return ''
			}
			if (!weekdayData.is_open) {
				return 'Closed'
			} else if (weekdayData.open24) {
				return 'Open 24 Hours'
			} else {
				return `${this.timeConverter(weekdayData.open)} - ${this.timeConverter(weekdayData.close)}`
			}
		},
		timeConverter(seconds) {
			if (seconds > 86400) { // > 24 hours
				seconds -= 86400
			}
			let hours = Math.floor(seconds / 3600)
			seconds -= hours * 3600
			const minutes = Math.floor(seconds / 60)
			let ampm = 'am'
			if (hours == 24) {
				hours -= 12
			} else if (hours == 12) {
				ampm = 'pm'
			} else if (hours == 0) {
				hours = 12
				ampm = 'am'
			} else if (hours > 12) {
				hours -= 12
				ampm = 'pm'
			}
			return hours + ':' + (minutes < 10 ? '0' : '') + minutes + ampm
		},
		reportIsValid(report) {
			return report?.email.length &&
				report?.reason.length &&
				report?.report.length
		},
		async handleReportSubmitted(report) {
			try {
				if (!this.reportIsValid(report)) {
					throw new Error('Report is missing information')
				}
				const { data } = await this.reportBusiness({
					...report,
					business: this.business.listing_id
				})
				if (!data?.success) {
					throw new Error('There was a problem. Please try again.')
				}
				if (data && data.success) {
					this.showToast({
						primaryText: 'Report Sumbission Successful',
						secondaryText: 'This business has been reported. Thank you for your assistance.',
						type: SUCCESS
					})
				}
			} catch (error) {
				this.showToast({
					primaryText: 'Server Error',
					secondaryText: 'There was a problem submitting your report, please try again',
					type: ERROR
				})
				logError(error)
			}
		}
	}
}
</script>
